import { Component, OnInit, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../_services/auth.service';
import { Globals } from '../../_globals';
import { Subscription } from 'rxjs';
import { EmergencyIncidentBadgeState, NotificationBadgeService, WorkOrderCountBadgeState, WorkflowItemBadgeState, } from 'app/_services/notification-badge.service';
import { NgbDropdown, NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Howl } from 'howler';
import { Title } from '@angular/platform-browser';
import { NotificationService } from 'app/_services/notification.service';
import { AdminCacheService } from 'app/_services/admin-cache.service';
import { ChangePasswordModalComponent } from 'app/user-profile/change-password-modal/change-password-modal.component';
import { environment } from 'environments/environment';
import { WorkflowAccessService } from 'app/_services/workflow-access.service';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
})
export class NavbarComponent implements OnInit, OnDestroy {
  @ViewChild('userDropdown', { static: true }) public userDropdown: NgbDropdown;

  private sidebar_menu_visible: any = 0;
  private toggleButton: any;
  private sidebarVisible: boolean;

  public isCollapsed = true;
  public role: any;
  public routeRole: any;

  public hasWorkOrderApprovalRole = false;
  public hasWorkflowApprovalRole = false;
  public isAdmin = false;
  public emergencyIncidentPollingSubscription: Subscription;
  public actionRequiredWorkOrderPollingSubscription: Subscription;
  public pendingWorkflowItemPollingSubscription: Subscription;
  public actionRequiredWorkOrdersCount = 0;
  public actionRequiredWorkOrdersCountUnknown = false;
  public workflowItemsCount = 0;
  public pendingWorkflowItemCountUnknown = false;
  public badgeGrowClass = false;
  public badgeGrowClassWorkflowItems = false;
  public tooltipMessage: string;
  public liveEmergencyIncident;

  private notificationsFetchedOnce = false;
  private workflowItemsNotificationsFetchedOnce = false;
  private oldWorkflowItemsCount = 0;
  private notificationSound: any;
  private sirenSound: any;
  public notificationSoundOn: boolean;

  constructor(
    private element: ElementRef,
    private router: Router,
    private authService: AuthService,
    private notificationBadgeService: NotificationBadgeService,
    public globals: Globals,
    private titleService: Title,
    private adminCacheService: AdminCacheService,
    private notificationService: NotificationService,
    private modalService: NgbModal,
    private workflowAccessService: WorkflowAccessService,
  ) {
    this.role = globals.user.role;
    this.routeRole = globals.user.routeRole;

    if (this.role === 4 || this.role === 5) {
      this.isAdmin = true;
    }

    if (this.role === 3 || this.role === 4 || this.role === 5 || this.role === 7 || this.role === 11 || this.role === 12) {
      this.hasWorkOrderApprovalRole = true;
    }

    const html = document.getElementsByTagName('html')[0];
    html.classList.add('nav-close');
    html.classList.remove('nav-open');
    this.verifyChangePasswordRequired();
  }

  ngOnInit() {
    const navbar: HTMLElement = this.element.nativeElement;
    this.toggleButton = navbar.getElementsByClassName('navbar-toggler')[0];

    // listen to all route navigation
    this.router.events.subscribe((event) => {
      this.onRouteNavigation();
    });

    this.sidebarClose();
    this.setupUiOptions();
    this.notificationSound = new Howl({
      src: ['assets/audio/notification.wav'],
    });
    this.sirenSound = new Howl({
      src: ['assets/audio/siren.ogg'],
    });

    this.addActionRequiredWorkOrderBadge();
    this.checkWorkflowApprovalRole();
    this.addPendingWorkflowItemBadge();

    if (localStorage.getItem('ui.admin.banner')) {
      document.body.classList.add('admin-view-as-banner');
    }
  }

  private onRouteNavigation() {
    // collapse top-right navbar links on nav to new page
    this.navbarLinksToggle(true);

    // If mobile, close sidebar. For wider screen the sidebar can be pinned
    if (innerWidth < 576) {
      this.sidebarClose();
    }

    const $layer: any = document.getElementsByClassName('close-layer')[0];
    if ($layer) {
      $layer.remove();
      this.sidebar_menu_visible = 0;
    }
  }

  ngOnDestroy() {
    if (this.actionRequiredWorkOrderPollingSubscription) {
      this.actionRequiredWorkOrderPollingSubscription.unsubscribe();
    }

    if (this.pendingWorkflowItemPollingSubscription) {
      this.pendingWorkflowItemPollingSubscription.unsubscribe();
    }

    if(this.emergencyIncidentPollingSubscription) {
      this.emergencyIncidentPollingSubscription.unsubscribe();
    }

    this.titleService.setTitle(`Skylark Control`);
  }

  private checkWorkflowApprovalRole() {
    // admin and call center
    if (this.role === 4 || this.role === 5) {
      this.hasWorkflowApprovalRole = true;
      this.addEmergencyIncidentBadge();
      return;
    }

    // site owner
    if (this.role === 3) {
      this.hasWorkflowApprovalRole = true;
      this.addEmergencyIncidentBadge();
      return;
    }

    // power station owner
    if (this.role === 9) {
      this.hasWorkflowApprovalRole = true;
      return;
    }

    // authorising officer
    if (this.role === 13) {
      this.hasWorkflowApprovalRole = !!this.globals.user.canEditCompetencies;
      return;
    }

    // site delegate (includes SSE control admin / regional manager who have no
    // AUTHORISING_OFFICER role). Show the inbox icon whenever they have any
    // visible workflow category (e.g. contractor certificates, restrictions) -
    // not only when they can edit competencies. getVisibleWorkflowCategories()
    if (this.role === 11) {
      this.hasWorkflowApprovalRole = false;
      this.addEmergencyIncidentBadge();
      return;
    }

    if (this.role === 7 || this.role === 12) {
      this.hasWorkflowApprovalRole = this.workflowAccessService.getVisibleWorkflowCategories().length > 0;
      this.addEmergencyIncidentBadge();
    }
  }

  private addPendingWorkflowItemBadge() {
    if (environment.devModeDisableGlobalPolling) {
      return;
    }

    if (!this.hasWorkflowApprovalRole) {
      return;
    }

    this.pendingWorkflowItemCountUnknown = true;
    this.pendingWorkflowItemPollingSubscription = this.notificationBadgeService
      .getPendingWorkflowItemsCountPrimary()
      .subscribe((badgeState: WorkflowItemBadgeState) => {
        if (badgeState.status === 'unknown') {
          this.pendingWorkflowItemCountUnknown = true;
          this.workflowItemsCount = 0;
          this.updateDocumentTitle();
          return;
        }

        const oldCount = this.oldWorkflowItemsCount;
        const newCount = badgeState.totalRecords || 0;

        this.pendingWorkflowItemCountUnknown = false;
        this.workflowItemsCount = newCount;

        if (newCount > 0) {
          if (this.workflowItemsNotificationsFetchedOnce && newCount > oldCount) {
            this.animatePendingWorkflowItemCount();
          }

          if (this.notificationSoundOn && this.workflowItemsNotificationsFetchedOnce && newCount > oldCount) {
            this.notificationSound.play();
          }
        }

        this.updateDocumentTitle();
        this.oldWorkflowItemsCount = newCount;
        this.workflowItemsNotificationsFetchedOnce = true;
      });
  }

  private addActionRequiredWorkOrderBadge() {
    if (environment.devModeDisableGlobalPolling) {
      return;
    }

    if (this.hasWorkOrderApprovalRole) {
      this.actionRequiredWorkOrdersCountUnknown = true;
      this.actionRequiredWorkOrderPollingSubscription = this.notificationBadgeService
        .getActionRequiredWorkOrderCountPrimary()
        .subscribe((badgeState: WorkOrderCountBadgeState) => {
          const oldCount = this.actionRequiredWorkOrdersCount;

          if (badgeState.status === 'unknown') {
            this.actionRequiredWorkOrdersCountUnknown = true;
            this.tooltipMessage = 'Count unavailable. Refresh page if this persists.';
            this.updateDocumentTitle();
            return;
          }

          const newCount = badgeState.count || 0;
          this.actionRequiredWorkOrdersCountUnknown = false;
          this.actionRequiredWorkOrdersCount = newCount;
          this.tooltipMessage = `${newCount} ${newCount === 1 ? 'work order' : 'work orders'}  require action`;

          if (newCount > 0) {
            this.animatePendingWorkOrderCount();

            // Play sound:
            // - if not muted
            // - if not just loading page for first time
            // - if the count has increased
            if (this.notificationSoundOn && this.notificationsFetchedOnce && newCount > oldCount) {
              this.notificationSound.play();
            }
          }

          this.updateDocumentTitle();
          this.notificationsFetchedOnce = true;
        });
    }
  }

  private updateDocumentTitle() {
    if (this.actionRequiredWorkOrdersCountUnknown || this.pendingWorkflowItemCountUnknown) {
      this.titleService.setTitle(`(!) Skylark Control`);
      return;
    }

    const totalCount = this.workflowItemsCount + this.actionRequiredWorkOrdersCount;
    this.titleService.setTitle(totalCount > 0 ? `(${totalCount}) Skylark Control` : 'Skylark Control');
  }

  private addEmergencyIncidentBadge() {
      if (environment.devModeDisableGlobalPolling) {
        return;
      }

      this.emergencyIncidentPollingSubscription = this.notificationBadgeService.getEmergencyIncidentCountPrimary().subscribe((badgeState: EmergencyIncidentBadgeState) => {
        if (badgeState.status === 'unknown') {
          return;
        }

        const incidents = badgeState.incidents || [];

        if (incidents.length) {
          this.liveEmergencyIncident = incidents[0];
          if (this.notificationSoundOn) {
            this.sirenSound.play();
          }
        } else {
          this.liveEmergencyIncident = null;
        }
      });
  }

  // Top-right user menu toggled
  // Also called every time the route is changed
  public navbarLinksToggle(shouldCollapse?: boolean) {
    this.isCollapsed = shouldCollapse !== undefined ? shouldCollapse : !this.isCollapsed;
    const navbar = document.getElementsByTagName('nav')[0];

    if (!this.isCollapsed) {
      navbar.classList.remove('navbar-transparent');
      navbar.classList.add('bg-white');
      this.userDropdown.open();

      this.userDropdown.autoClose = false;
      this.userDropdown.open();
    } else {
      navbar.classList.add('navbar-transparent');
      navbar.classList.remove('bg-white');
      this.userDropdown.autoClose = true;
      this.userDropdown.close();
    }
  }

  private sidebarOpen() {
    const toggleButton = this.toggleButton;
    const mainPanel = <HTMLElement>document.getElementsByClassName('main-panel')[0];

    if (window.innerWidth < 576) {
      mainPanel.style.position = 'fixed';
    }

    if (toggleButton) {
      toggleButton.classList.add('toggled');
    }

    const html = document.getElementsByTagName('html')[0];
    html.classList.add('nav-open');
    html.classList.remove('nav-close');

    this.sidebarVisible = true;
  }

  private sidebarClose() {
    const toggleButton = this.toggleButton;
    const mainPanel = <HTMLElement>document.getElementsByClassName('main-panel')[0];

    if (window.innerWidth < 576) {
      setTimeout(function () {
        if (mainPanel) {
          mainPanel.style.position = '';
        }
      }, 500);
    }

    if (toggleButton) {
      toggleButton.classList.remove('toggled');
    }

    const html = document.getElementsByTagName('html')[0];
    html.classList.add('nav-close');
    html.classList.remove('nav-open');

    this.sidebarVisible = false;
  }

  public sidebarToggle() {
    if (this.sidebarVisible === false) {
      this.sidebarOpen();
    } else {
      this.sidebarClose();
    }

    this.manipulateExtraLayerDiv();
  }

  // I'm not sure why this code exists.
  // It adds a div when the sidebar is closed, and removes it when open.
  // It could be something to do with adding room for a scrollbar.
  private manipulateExtraLayerDiv() {
    let $layer = null;

    if (this.sidebar_menu_visible === 1) {
      $layer = document.getElementsByClassName('close-layer')[0];
      if ($layer) {
        $layer.remove();
      }
      this.sidebar_menu_visible = 0;
    } else {
      $layer = document.createElement('div');
      $layer.setAttribute('class', 'close-layer');

      const html = document.getElementsByTagName('html')[0];
      if (html.querySelectorAll('.main-panel')) {
        document.getElementsByClassName('main-panel')[0].appendChild($layer);
      } else if (html.classList.contains('off-canvas-sidebar')) {
        document.getElementsByClassName('wrapper-full-page')[0].appendChild($layer);
      }

      setTimeout(function () {
        $layer.classList.add('visible');
      }, 100);

      $layer.onclick = function () {
        // asign a function
        this.mobile_menu_visible = 0;
        $layer.classList.remove('visible');
        setTimeout(function () {
          $layer.remove();
        }, 400);
      }.bind(this);

      this.sidebar_menu_visible = 1;
    }
  }

  public logout() {
    this.authService.logout();
  }

  private animatePendingWorkOrderCount() {
    this.badgeGrowClass = true;
    setTimeout(() => {
      this.badgeGrowClass = false;
    }, 500);
  }

  private animatePendingWorkflowItemCount() {
    this.badgeGrowClassWorkflowItems = true;
    setTimeout(() => {
      this.badgeGrowClassWorkflowItems = false;
    }, 500);
  }

  public setupUiOptions() {
    const savedOptions = localStorage.getItem('ui.options.notificationSoundOn');

    if (savedOptions) {
      this.notificationSoundOn = JSON.parse(savedOptions);
    } else {
      this.notificationSoundOn = true;
    }
  }

  public toggleNotificationSound(on: boolean) {
    this.notificationSoundOn = on;

    if (on) {
      this.notificationSound.play();
    }
    localStorage.setItem('ui.options.notificationSoundOn', JSON.stringify(this.notificationSoundOn));
  }

  public clearCache() {
    this.adminCacheService.clearCache().subscribe(() => {
      this.notificationService.notify('success', 'Cache cleared');
    }, error => {
      console.error(error);
      this.notificationService.notify('danger', 'Error trying to clear cache');
    });
  }

  public openEmergencyIncident() {
    this.router.navigate(['/' + this.routeRole + '/sites/' + this.liveEmergencyIncident.siteId + '/site-incidents']);
  }

  public verifyChangePasswordRequired () {
    let isPasswordChangeRequired = JSON.parse(localStorage.getItem(environment.keys.changePasswordRequired));

    if (isPasswordChangeRequired){
      this.openChangePassword();
    }
  }

  public openChangePassword() {
    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static', keyboard: false };
    const modal = this.modalService.open(ChangePasswordModalComponent, modalOptions);
    modal.result.then(result => {
      if (result === 'refresh') {
        this.verifyChangePasswordRequired();
      }
    }).catch(() => { /* modal dismissed */ });
  }
}
